
import type { ReactNode, ComponentType } from "react";
import { useUiStore } from "@/store/ui";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Logo } from "@/components/brand/logo";
import { BrandWordmark } from "@/components/brand/wordmark";
import { useTranslation } from "react-i18next";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import {
  Shield,
  FileText,
  Lock,
  LifeBuoy,
  ArrowLeft,
  Mail,
  Phone,
  Clock,
  MapPin,
  Package,
  Truck,
  ShoppingCart,
  Pill,
  Droplets,
  Banknote,
  MessageSquare,
  CreditCard,
  UserCog,
  Flag,
  Scale,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Globe,
  Building2,
  Bell,
  Navigation,
  Star,
  ArrowRight,
} from "lucide-react";
import { useAuthStore } from "@/store/auth";

// ============================================================================
// Shared internal layout helpers (not exported)
// ============================================================================

function LegalHeader({
  title,
  icon: Icon,
}: {
  title: string;
  icon: ComponentType<{ className?: string }>;
}) {
  const navigate = useUiStore((s) => s.navigate);
  const { t } = useTranslation();
  return (
    <header className="sticky top-0 z-30 border-b border-border/60 bg-background/80 backdrop-blur-lg">
      <div className="mx-auto flex h-14 max-w-3xl items-center gap-2 px-4">
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 shrink-0"
          onClick={() => navigate("")}
          aria-label={t("legal.backToHome")}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <Icon className="h-5 w-5 shrink-0 text-primary" />
        <span className="truncate text-base font-semibold sm:text-lg">{title}</span>
        <div className="ml-auto flex items-center gap-1.5">
          <Logo className="h-7 w-7" />
          <BrandWordmark className="hidden text-sm sm:inline" />
        </div>
      </div>
    </header>
  );
}

function LegalFooter() {
  const { t } = useTranslation();
  return (
    <footer className="mt-auto border-t border-border/60 bg-muted/30">
      <div className="mx-auto flex max-w-3xl flex-col gap-1 px-4 py-6 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
        <span>{t("legal.footerCopyright")}</span>
        <span className="sm:text-right">HornDeliver <span className="text-gold font-medium">{t("legal.footerVersion")}</span></span>
      </div>
    </footer>
  );
}

function LegalPage({
  title,
  icon,
  updated,
  intro,
  children,
}: {
  title: string;
  icon: ComponentType<{ className?: string }>;
  updated?: string;
  intro?: ReactNode;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-screen flex-col">
      <LegalHeader title={title} icon={icon} />
      <main className="flex-1">
        <div className="mx-auto max-w-3xl space-y-4 px-4 py-6">
          <div className="space-y-2">
            <h1 className="text-2xl font-bold">{title}</h1>
            {updated && (
              <p className="text-sm text-muted-foreground">{t("legal.lastUpdatedPrefix")} {updated}</p>
            )}
            {intro && (
              <p className="text-base leading-relaxed text-muted-foreground">{intro}</p>
            )}
          </div>
          {children}
        </div>
      </main>
      <LegalFooter />
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card>
      <CardContent className="space-y-3 p-6">
        <h2 className="text-xl font-semibold mb-2">{title}</h2>
        {children}
      </CardContent>
    </Card>
  );
}

function P({ children, className }: { children: ReactNode; className?: string }) {
  return <p className={`text-base leading-relaxed text-muted-foreground ${className || ""}`}>{children}</p>;
}

function UL({ children }: { children: ReactNode }) {
  return (
    <ul className="list-disc space-y-1 pl-6 text-base leading-relaxed text-muted-foreground">
      {children}
    </ul>
  );
}

function ContactBadge({ icon: Icon, children }: { icon: ComponentType<{ className?: string }>; children: ReactNode }) {
  return (
    <Badge variant="secondary" className="gap-1.5 py-1">
      <Icon className="h-3.5 w-3.5" />
      {children}
    </Badge>
  );
}

// ============================================================================
// Privacy Policy
// ============================================================================

export function PrivacyPolicyView() {
  const { t } = useTranslation();
  return (
    <LegalPage
      title={t("legal.privacyTitle")}
      icon={Shield}
      updated="2026-01-15"
      intro={t("legal.privacy.intro")}
    >
      <Section title={t("legal.privacy.s1Title")}>
        <P>{t("legal.privacy.s1Body")}</P>
        <h3 className="text-base font-semibold mt-4 mb-1 text-foreground">{t("legal.privacy.s1AccountInfoTitle")}</h3>
        <UL>
          <li>{t("legal.privacy.s1AccountInfo1")}</li>
          <li>{t("legal.privacy.s1AccountInfo2")}</li>
          <li>{t("legal.privacy.s1AccountInfo3")}</li>
          <li>{t("legal.privacy.s1AccountInfo4")}</li>
        </UL>
        <h3 className="text-base font-semibold mt-4 mb-1 text-foreground">{t("legal.privacy.s1IdDocsTitle")}</h3>
        <UL>
          <li>{t("legal.privacy.s1IdDocs1")}</li>
          <li>{t("legal.privacy.s1IdDocs2")}</li>
          <li>{t("legal.privacy.s1IdDocs3")}</li>
        </UL>
        <h3 className="text-base font-semibold mt-4 mb-1 text-foreground">{t("legal.privacy.s1UsageTitle")}</h3>
        <UL>
          <li>{t("legal.privacy.s1Usage1")}</li>
          <li>{t("legal.privacy.s1Usage2")}</li>
          <li>{t("legal.privacy.s1Usage3")}</li>
          <li>{t("legal.privacy.s1Usage4")}</li>
        </UL>
        <h3 className="text-base font-semibold mt-4 mb-1 text-foreground">{t("legal.privacy.s1DeviceTitle")}</h3>
        <UL>
          <li>{t("legal.privacy.s1Device1")}</li>
          <li>{t("legal.privacy.s1Device2")}</li>
        </UL>
      </Section>

      <Section title={t("legal.privacy.s2Title")}>
        <P>{t("legal.privacy.s2Body")}</P>
        <UL>
          <li><strong className="text-foreground">{t("legal.privacy.s2Item1")}</strong>{t("legal.privacy.s2Item1Desc")}</li>
          <li><strong className="text-foreground">{t("legal.privacy.s2Item2")}</strong>{t("legal.privacy.s2Item2Desc")}</li>
          <li><strong className="text-foreground">{t("legal.privacy.s2Item3")}</strong>{t("legal.privacy.s2Item3Desc")}</li>
          <li><strong className="text-foreground">{t("legal.privacy.s2Item4")}</strong>{t("legal.privacy.s2Item4Desc")}</li>
          <li><strong className="text-foreground">{t("legal.privacy.s2Item5")}</strong>{t("legal.privacy.s2Item5Desc")}</li>
          <li><strong className="text-foreground">{t("legal.privacy.s2Item6")}</strong>{t("legal.privacy.s2Item6Desc")}</li>
          <li><strong className="text-foreground">{t("legal.privacy.s2Item7")}</strong>{t("legal.privacy.s2Item7Desc")}</li>
        </UL>
      </Section>

      <Section title={t("legal.privacy.s3Title")}>
        <P>{t("legal.privacy.s3Body")} <strong className="text-foreground">{t("legal.privacy.s3BodyEmphasis")}</strong> {t("legal.privacy.s3BodySuffix")}</P>
        <UL>
          <li><strong className="text-foreground">{t("legal.privacy.s3Item1")}</strong>{t("legal.privacy.s3Item1Desc")}</li>
          <li><strong className="text-foreground">{t("legal.privacy.s3Item2")}</strong>{t("legal.privacy.s3Item2Desc")}</li>
          <li><strong className="text-foreground">{t("legal.privacy.s3Item3")}</strong>{t("legal.privacy.s3Item3Desc")}</li>
          <li><strong className="text-foreground">{t("legal.privacy.s3Item4")}</strong>{t("legal.privacy.s3Item4Desc")}</li>
        </UL>
        <P>{t("legal.privacy.s3AggregateNote")}</P>
      </Section>

      <Section title={t("legal.privacy.s4Title")}>
        <P>{t("legal.privacy.s4Body")}</P>
        <UL>
          <li><strong className="text-foreground">{t("legal.privacy.s4Item1")}</strong>{t("legal.privacy.s4Item1Desc")}</li>
          <li><strong className="text-foreground">{t("legal.privacy.s4Item2")}</strong>{t("legal.privacy.s4Item2Desc")}</li>
          <li><strong className="text-foreground">{t("legal.privacy.s4Item3")}</strong>{t("legal.privacy.s4Item3Desc")} <strong className="text-foreground">{t("legal.privacy.s4Item3Emphasis")}</strong> {t("legal.privacy.s4Item3Suffix")}</li>
          <li><strong className="text-foreground">{t("legal.privacy.s4Item4")}</strong>{t("legal.privacy.s4Item4Desc")}</li>
          <li><strong className="text-foreground">{t("legal.privacy.s4Item5")}</strong>{t("legal.privacy.s4Item5Desc")}</li>
        </UL>
      </Section>

      <Section title={t("legal.privacy.s5Title")}>
        <P>{t("legal.privacy.s5Body")} <a href="mailto:support@HornDeliver.app" className="font-medium text-primary underline">support@HornDeliver.app</a>{t("legal.privacy.s5BodySuffix")}</P>
        <UL>
          <li><strong className="text-foreground">{t("legal.privacy.s5Item1")}</strong>{t("legal.privacy.s5Item1Desc")}</li>
          <li><strong className="text-foreground">{t("legal.privacy.s5Item2")}</strong>{t("legal.privacy.s5Item2Desc")}</li>
          <li><strong className="text-foreground">{t("legal.privacy.s5Item3")}</strong>{t("legal.privacy.s5Item3Desc")}</li>
          <li><strong className="text-foreground">{t("legal.privacy.s5Item4")}</strong>{t("legal.privacy.s5Item4Desc")}</li>
          <li><strong className="text-foreground">{t("legal.privacy.s5Item5")}</strong>{t("legal.privacy.s5Item5Desc")}</li>
          <li><strong className="text-foreground">{t("legal.privacy.s5Item6")}</strong>{t("legal.privacy.s5Item6Desc")}</li>
        </UL>
        <P>{t("legal.privacy.s5ClosingNote")}</P>
      </Section>

      <Section title={t("legal.privacy.s6Title")}>
        <P>{t("legal.privacy.s6Body")}</P>
        <UL>
          <li><strong className="text-foreground">{t("legal.privacy.s6Item1")}</strong>{t("legal.privacy.s6Item1Desc")}</li>
          <li><strong className="text-foreground">{t("legal.privacy.s6Item2")}</strong>{t("legal.privacy.s6Item2Desc")}</li>
          <li><strong className="text-foreground">{t("legal.privacy.s6Item3")}</strong>{t("legal.privacy.s6Item3Desc")}</li>
          <li><strong className="text-foreground">{t("legal.privacy.s6Item4")}</strong>{t("legal.privacy.s6Item4Desc")}</li>
          <li><strong className="text-foreground">{t("legal.privacy.s6Item5")}</strong>{t("legal.privacy.s6Item5Desc")}</li>
          <li><strong className="text-foreground">{t("legal.privacy.s6Item6")}</strong>{t("legal.privacy.s6Item6Desc")}</li>
          <li><strong className="text-foreground">{t("legal.privacy.s6Item7")}</strong>{t("legal.privacy.s6Item7Desc")}</li>
        </UL>
        <P>{t("legal.privacy.s6ClosingNote")}</P>
      </Section>

      <Section title={t("legal.privacy.s7Title")}>
        <P>{t("legal.privacy.s7Body1")} <strong className="text-foreground">{t("legal.privacy.s7Body1Emphasis")}</strong> {t("legal.privacy.s7Body1Suffix")}</P>
        <P>{t("legal.privacy.s7Body2")} <a href="mailto:support@HornDeliver.app" className="font-medium text-primary underline">support@HornDeliver.app</a>{t("legal.privacy.s7Body2Suffix")}</P>
      </Section>

      <Section title={t("legal.privacy.s8Title")}>
        <P>{t("legal.privacy.s8Body")}</P>
        <UL>
          <li>{t("legal.privacy.s8Item1")}</li>
          <li>{t("legal.privacy.s8Item2")}</li>
          <li>{t("legal.privacy.s8Item3")} <a href="mailto:support@HornDeliver.app" className="font-medium text-primary underline">support@HornDeliver.app</a>.</li>
        </UL>
        <P>{t("legal.privacy.s8ClosingNote")}</P>
      </Section>

      <Section title={t("legal.privacy.s9Title")}>
        <P>{t("legal.privacy.s9Body")}</P>
        <div className="flex flex-wrap gap-2 pt-1">
          <a href="mailto:support@HornDeliver.app">
            <ContactBadge icon={Mail}>support@HornDeliver.app</ContactBadge>
          </a>
          <ContactBadge icon={Building2}>{t("legal.privacy.s9HornDigital")}</ContactBadge>
          <ContactBadge icon={MapPin}>{t("legal.privacy.s9Plateau")}</ContactBadge>
        </div>
      </Section>
    </LegalPage>
  );
}

// ============================================================================
// Terms & Trust
// ============================================================================

function Part({ label, title, children }: { label: string; title: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 border-b border-emerald-500/25 pb-2">
        <Badge variant="outline" className="border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400">
          {label}
        </Badge>
        <h2 className="text-lg font-bold tracking-tight text-foreground sm:text-xl">{title}</h2>
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

export function TermsTrustView() {
  const { t } = useTranslation();
  return (
    <LegalPage
      title={t("legal.termsTitle")}
      icon={FileText}
      updated="2026-08-01"
      intro={t("legal.terms.intro")}
    >
      <Card>
        <CardContent className="space-y-3 p-6">
          <P>{t("legal.terms.preamble1")}</P>
          <P><strong className="text-foreground">{t("legal.terms.preamble2")}</strong> {t("legal.terms.preamble2Suffix")}</P>
          <P>{t("legal.terms.preamble3")}</P>
        </CardContent>
      </Card>

      <Part label={t("legal.terms.part1Label")} title={t("legal.terms.part1Title")}>
        <Section title={t("legal.terms.s1Title")}>
          <UL>
            <li><strong className="text-foreground">{t("legal.terms.s1Def1")}</strong> {t("legal.terms.s1Def1Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s1Def2")}</strong> {t("legal.terms.s1Def2Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s1Def3")}</strong> {t("legal.terms.s1Def3Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s1Def4")}</strong> {t("legal.terms.s1Def4Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s1Def5")}</strong> {t("legal.terms.s1Def5Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s1Def6")}</strong> {t("legal.terms.s1Def6Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s1Def7")}</strong> {t("legal.terms.s1Def7Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s1Def8")}</strong> {t("legal.terms.s1Def8Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s1Def9")}</strong> {t("legal.terms.s1Def9Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s1Def10")}</strong> {t("legal.terms.s1Def10Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s1Def11")}</strong> {t("legal.terms.s1Def11Desc")}</li>
          </UL>
        </Section>

        <Section title={t("legal.terms.s2Title")}>
          <UL>
            <li><strong className="text-foreground">{t("legal.terms.s2Item1")}</strong>{t("legal.terms.s2Item1Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s2Item2")}</strong>{t("legal.terms.s2Item2Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s2Item3")}</strong>{t("legal.terms.s2Item3Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s2Item4")}</strong>{t("legal.terms.s2Item4Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s2Item5")}</strong>{t("legal.terms.s2Item5Desc")}</li>
          </UL>
        </Section>

        <Section title={t("legal.terms.s3Title")}>
          <P>{t("legal.terms.s3Body")} <strong className="text-foreground">{t("legal.terms.s3BodyEmphasis")}</strong> {t("legal.terms.s3BodySuffix")}</P>
          <UL>
            <li><strong className="text-foreground">{t("legal.terms.s3Item1")}</strong>{t("legal.terms.s3Item1Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s3Item2")}</strong>{t("legal.terms.s3Item2Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s3Item3")}</strong>{t("legal.terms.s3Item3Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s3Item4")}</strong>{t("legal.terms.s3Item4Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s3Item5")}</strong>{t("legal.terms.s3Item5Desc")}</li>
          </UL>
        </Section>

        <Section title={t("legal.terms.s4Title")}>
          <UL>
            <li><strong className="text-foreground">{t("legal.terms.s4Item1")}</strong>{t("legal.terms.s4Item1Desc")}
              <ul className="mt-1.5 list-disc space-y-1 pl-6 text-base leading-relaxed text-muted-foreground">
                <li>{t("legal.terms.s4Item1Sub1")}</li>
                <li>{t("legal.terms.s4Item1Sub2")}</li>
                <li>{t("legal.terms.s4Item1Sub3")}</li>
                <li>{t("legal.terms.s4Item1Sub4")}</li>
                <li>{t("legal.terms.s4Item1Sub5")}</li>
                <li>{t("legal.terms.s4Item1Sub6")}</li>
                <li>{t("legal.terms.s4Item1Sub7")}</li>
              </ul>
            </li>
            <li><strong className="text-foreground">{t("legal.terms.s4Item2")}</strong>{t("legal.terms.s4Item2Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s4Item3")}</strong>{t("legal.terms.s4Item3Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s4Item4")}</strong>{t("legal.terms.s4Item4Desc")}</li>
          </UL>
        </Section>

        <Section title={t("legal.terms.s5Title")}>
          <UL>
            <li><strong className="text-foreground">{t("legal.terms.s5Item1")}</strong>{t("legal.terms.s5Item1Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s5Item2")}</strong>{t("legal.terms.s5Item2Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s5Item3")}</strong>{t("legal.terms.s5Item3Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s5Item4")}</strong>{t("legal.terms.s5Item4Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s5Item5")}</strong>{t("legal.terms.s5Item5Desc")}</li>
          </UL>
        </Section>

        <Section title={t("legal.terms.s6Title")}>
          <UL>
            <li><strong className="text-foreground">{t("legal.terms.s6Item1")}</strong>{t("legal.terms.s6Item1Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s6Item2")}</strong>{t("legal.terms.s6Item2Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s6Item3")}</strong>{t("legal.terms.s6Item3Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s6Item4")}</strong>{t("legal.terms.s6Item4Desc")}</li>
          </UL>
        </Section>

        <Section title={t("legal.terms.s7Title")}>
          <UL>
            <li><strong className="text-foreground">{t("legal.terms.s7Item1")}</strong>{t("legal.terms.s7Item1Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s7Item2")}</strong>{t("legal.terms.s7Item2Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s7Item3")}</strong>{t("legal.terms.s7Item3Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s7Item4")}</strong>{t("legal.terms.s7Item4Desc")}</li>
          </UL>
        </Section>

        <Section title={t("legal.terms.s8Title")}>
          <UL>
            <li><strong className="text-foreground">{t("legal.terms.s8Item1")}</strong>{t("legal.terms.s8Item1Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s8Item2")}</strong>{t("legal.terms.s8Item2Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s8Item3")}</strong>{t("legal.terms.s8Item3Desc")}</li>
          </UL>
        </Section>

        <Section title={t("legal.terms.s9Title")}>
          <UL>
            <li><strong className="text-foreground">{t("legal.terms.s9Item1")}</strong>{t("legal.terms.s9Item1Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s9Item2")}</strong>{t("legal.terms.s9Item2Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s9Item3")}</strong>{t("legal.terms.s9Item3Desc")}</li>
          </UL>
        </Section>

        <Section title={t("legal.terms.s10Title")}>
          <UL>
            <li><strong className="text-foreground">{t("legal.terms.s10Item1")}</strong>{t("legal.terms.s10Item1Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s10Item2")}</strong>{t("legal.terms.s10Item2Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s10Item3")}</strong>{t("legal.terms.s10Item3Desc")}</li>
          </UL>
        </Section>

        <Section title={t("legal.terms.s11Title")}>
          <P>{t("legal.terms.s11Body")}</P>
          <UL>
            <li>{t("legal.terms.s11Item1")}</li>
            <li>{t("legal.terms.s11Item2")}</li>
            <li>{t("legal.terms.s11Item3")}</li>
            <li>{t("legal.terms.s11Item4")}</li>
          </UL>
        </Section>

        <Section title={t("legal.terms.s12Title")}>
          <P>{t("legal.terms.s12Body")}</P>
        </Section>

        <Section title={t("legal.terms.s13Title")}>
          <P>{t("legal.terms.s13Body")}</P>
        </Section>
      </Part>

      <Part label={t("legal.terms.part2Label")} title={t("legal.terms.part2Title")}>
        <Section title={t("legal.terms.s14Title")}>
          <UL>
            <li><strong className="text-foreground">{t("legal.terms.s14Item1")}</strong>{t("legal.terms.s14Item1Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s14Item2")}</strong>{t("legal.terms.s14Item2Desc")}
              <ul className="mt-1.5 list-disc space-y-1 pl-6 text-base leading-relaxed text-muted-foreground">
                <li>{t("legal.terms.s14Item2Sub1")}</li>
                <li>{t("legal.terms.s14Item2Sub2")}</li>
                <li>{t("legal.terms.s14Item2Sub3")}</li>
                <li>{t("legal.terms.s14Item2Sub4")}</li>
              </ul>
            </li>
            <li><strong className="text-foreground">{t("legal.terms.s14Item3")}</strong>{t("legal.terms.s14Item3Desc")}
              <ul className="mt-1.5 list-disc space-y-1 pl-6 text-base leading-relaxed text-muted-foreground">
                <li>{t("legal.terms.s14Item3Sub1")}</li>
                <li>{t("legal.terms.s14Item3Sub2")}</li>
                <li>{t("legal.terms.s14Item3Sub3")}</li>
                <li>{t("legal.terms.s14Item3Sub4")}</li>
              </ul>
            </li>
            <li><strong className="text-foreground">{t("legal.terms.s14Item4")}</strong>{t("legal.terms.s14Item4Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s14Item5")}</strong>{t("legal.terms.s14Item5Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s14Item6")}</strong>{t("legal.terms.s14Item6Desc")}</li>
          </UL>
        </Section>

        <Section title={t("legal.terms.s15Title")}>
          <UL>
            <li><strong className="text-foreground">{t("legal.terms.s15Item1")}</strong>{t("legal.terms.s15Item1Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s15Item2")}</strong>{t("legal.terms.s15Item2Desc")}
              <ul className="mt-1.5 list-disc space-y-1 pl-6 text-base leading-relaxed text-muted-foreground">
                <li>{t("legal.terms.s15Item2Sub1")}</li>
                <li>{t("legal.terms.s15Item2Sub2")}</li>
                <li>{t("legal.terms.s15Item2Sub3")}</li>
                <li>{t("legal.terms.s15Item2Sub4")}</li>
              </ul>
            </li>
            <li><strong className="text-foreground">{t("legal.terms.s15Item3")}</strong>{t("legal.terms.s15Item3Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s15Item4")}</strong>{t("legal.terms.s15Item4Desc")}
              <ul className="mt-1.5 list-disc space-y-1 pl-6 text-base leading-relaxed text-muted-foreground">
                <li>{t("legal.terms.s15Item4Sub1")}</li>
                <li>{t("legal.terms.s15Item4Sub2")}</li>
                <li>{t("legal.terms.s15Item4Sub3")}</li>
                <li>{t("legal.terms.s15Item4Sub4")}</li>
              </ul>
            </li>
            <li><strong className="text-foreground">{t("legal.terms.s15Item5")}</strong>{t("legal.terms.s15Item5Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s15Item6")}</strong>{t("legal.terms.s15Item6Desc")}</li>
          </UL>
        </Section>

        <Section title={t("legal.terms.s16Title")}>
          <UL>
            <li><strong className="text-foreground">{t("legal.terms.s16Item1")}</strong>{t("legal.terms.s16Item1Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s16Item2")}</strong>{t("legal.terms.s16Item2Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s16Item3")}</strong>{t("legal.terms.s16Item3Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s16Item4")}</strong>{t("legal.terms.s16Item4Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s16Item5")}</strong>{t("legal.terms.s16Item5Desc")}</li>
          </UL>
        </Section>

        <Section title={t("legal.terms.s17Title")}>
          <UL>
            <li><strong className="text-foreground">{t("legal.terms.s17Item1")}</strong>{t("legal.terms.s17Item1Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s17Item2")}</strong>{t("legal.terms.s17Item2Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s17Item3")}</strong>{t("legal.terms.s17Item3Desc")}</li>
          </UL>
        </Section>
      </Part>

      <Part label={t("legal.terms.part3Label")} title={t("legal.terms.part3Title")}>
        <Section title={t("legal.terms.s18Title")}>
          <UL>
            <li><strong className="text-foreground">{t("legal.terms.s18Item1")}</strong>{t("legal.terms.s18Item1Desc")}
              <ul className="mt-1.5 list-disc space-y-1 pl-6 text-base leading-relaxed text-muted-foreground">
                <li>{t("legal.terms.s18Item1Sub1")}</li>
                <li>{t("legal.terms.s18Item1Sub2")}</li>
                <li>{t("legal.terms.s18Item1Sub3")}</li>
                <li>{t("legal.terms.s18Item1Sub4")}</li>
                <li>{t("legal.terms.s18Item1Sub5")}</li>
              </ul>
            </li>
            <li><strong className="text-foreground">{t("legal.terms.s18Item2")}</strong>{t("legal.terms.s18Item2Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s18Item3")}</strong>{t("legal.terms.s18Item3Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s18Item4")}</strong>{t("legal.terms.s18Item4Desc")}</li>
          </UL>
        </Section>

        <Section title={t("legal.terms.s19Title")}>
          <UL>
            <li><strong className="text-foreground">{t("legal.terms.s19Item1")}</strong>{t("legal.terms.s19Item1Desc")}
              <ul className="mt-1.5 list-disc space-y-1 pl-6 text-base leading-relaxed text-muted-foreground">
                <li>{t("legal.terms.s19Item1Sub1")}</li>
                <li>{t("legal.terms.s19Item1Sub2")}</li>
                <li>{t("legal.terms.s19Item1Sub3")}</li>
                <li>{t("legal.terms.s19Item1Sub4")}</li>
              </ul>
            </li>
            <li><strong className="text-foreground">{t("legal.terms.s19Item2")}</strong>{t("legal.terms.s19Item2Desc")}
              <ul className="mt-1.5 list-disc space-y-1 pl-6 text-base leading-relaxed text-muted-foreground">
                <li>{t("legal.terms.s19Item2Sub1")}</li>
                <li>{t("legal.terms.s19Item2Sub2")}</li>
                <li>{t("legal.terms.s19Item2Sub3")}</li>
                <li>{t("legal.terms.s19Item2Sub4")}</li>
              </ul>
            </li>
            <li><strong className="text-foreground">{t("legal.terms.s19Item3")}</strong>{t("legal.terms.s19Item3Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s19Item4")}</strong>{t("legal.terms.s19Item4Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s19Item5")}</strong>{t("legal.terms.s19Item5Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s19Item6")}</strong>{t("legal.terms.s19Item6Desc")}</li>
          </UL>
        </Section>

        <Section title={t("legal.terms.s20Title")}>
          <UL>
            <li><strong className="text-foreground">{t("legal.terms.s20Item1")}</strong>{t("legal.terms.s20Item1Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s20Item2")}</strong>{t("legal.terms.s20Item2Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s20Item3")}</strong>{t("legal.terms.s20Item3Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s20Item4")}</strong>{t("legal.terms.s20Item4Desc")}</li>
          </UL>
        </Section>

        <Section title={t("legal.terms.s21Title")}>
          <UL>
            <li><strong className="text-foreground">{t("legal.terms.s21Item1")}</strong>{t("legal.terms.s21Item1Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s21Item2")}</strong>{t("legal.terms.s21Item2Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s21Item3")}</strong>{t("legal.terms.s21Item3Desc")}</li>
          </UL>
        </Section>

        <Section title={t("legal.terms.s22Title")}>
          <UL>
            <li><strong className="text-foreground">{t("legal.terms.s22Item1")}</strong>{t("legal.terms.s22Item1Desc")}
              <ul className="mt-1.5 list-disc space-y-1 pl-6 text-base leading-relaxed text-muted-foreground">
                <li>{t("legal.terms.s22Item1Sub1")}</li>
                <li>{t("legal.terms.s22Item1Sub2")}</li>
                <li>{t("legal.terms.s22Item1Sub3")}</li>
                <li>{t("legal.terms.s22Item1Sub4")}</li>
              </ul>
            </li>
            <li><strong className="text-foreground">{t("legal.terms.s22Item2")}</strong>{t("legal.terms.s22Item2Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s22Item3")}</strong>{t("legal.terms.s22Item3Desc")}</li>
          </UL>
        </Section>
      </Part>

      <Part label={t("legal.terms.part4Label")} title={t("legal.terms.part4Title")}>
        <Section title={t("legal.terms.s23Title")}>
          <UL>
            <li><strong className="text-foreground">{t("legal.terms.s23Item1")}</strong>{t("legal.terms.s23Item1Desc")}
              <ul className="mt-1.5 list-disc space-y-1 pl-6 text-base leading-relaxed text-muted-foreground">
                <li>{t("legal.terms.s23Item1Sub1")}</li>
                <li>{t("legal.terms.s23Item1Sub2")}</li>
                <li>{t("legal.terms.s23Item1Sub3")}</li>
                <li>{t("legal.terms.s23Item1Sub4")}</li>
                <li>{t("legal.terms.s23Item1Sub5")}</li>
                <li>{t("legal.terms.s23Item1Sub6")}</li>
              </ul>
            </li>
            <li><strong className="text-foreground">{t("legal.terms.s23Item2")}</strong>{t("legal.terms.s23Item2Desc")}
              <ul className="mt-1.5 list-disc space-y-1 pl-6 text-base leading-relaxed text-muted-foreground">
                <li>{t("legal.terms.s23Item2Sub1")}</li>
                <li>{t("legal.terms.s23Item2Sub2")}</li>
                <li>{t("legal.terms.s23Item2Sub3")}</li>
              </ul>
            </li>
            <li><strong className="text-foreground">{t("legal.terms.s23Item3")}</strong>{t("legal.terms.s23Item3Desc")}</li>
          </UL>
        </Section>

        <Section title={t("legal.terms.s24Title")}>
          <UL>
            <li><strong className="text-foreground">{t("legal.terms.s24Item1")}</strong>{t("legal.terms.s24Item1Desc")}
              <ul className="mt-1.5 list-disc space-y-1 pl-6 text-base leading-relaxed text-muted-foreground">
                <li>{t("legal.terms.s24Item1Sub1")}</li>
                <li>{t("legal.terms.s24Item1Sub2")}</li>
                <li>{t("legal.terms.s24Item1Sub3")}</li>
                <li>{t("legal.terms.s24Item1Sub4")}</li>
                <li>{t("legal.terms.s24Item1Sub5")}</li>
                <li>{t("legal.terms.s24Item1Sub6")}</li>
              </ul>
            </li>
            <li><strong className="text-foreground">{t("legal.terms.s24Item2")}</strong>{t("legal.terms.s24Item2Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s24Item3")}</strong>{t("legal.terms.s24Item3Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s24Item4")}</strong>{t("legal.terms.s24Item4Desc")}
              <ul className="mt-1.5 list-disc space-y-1 pl-6 text-base leading-relaxed text-muted-foreground">
                <li>{t("legal.terms.s24Item4Sub1")}</li>
                <li>{t("legal.terms.s24Item4Sub2")}</li>
                <li>{t("legal.terms.s24Item4Sub3")}</li>
                <li>{t("legal.terms.s24Item4Sub4")}</li>
              </ul>
            </li>
            <li><strong className="text-foreground">{t("legal.terms.s24Item5")}</strong>{t("legal.terms.s24Item5Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s24Item6")}</strong>{t("legal.terms.s24Item6Desc")}</li>
          </UL>
        </Section>

        <Section title={t("legal.terms.s25Title")}>
          <UL>
            <li><strong className="text-foreground">{t("legal.terms.s25Item1")}</strong>{t("legal.terms.s25Item1Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s25Item2")}</strong>{t("legal.terms.s25Item2Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s25Item3")}</strong>{t("legal.terms.s25Item3Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s25Item4")}</strong>{t("legal.terms.s25Item4Desc")}</li>
          </UL>
        </Section>

        <Section title={t("legal.terms.s26Title")}>
          <UL>
            <li><strong className="text-foreground">{t("legal.terms.s26Item1")}</strong>{t("legal.terms.s26Item1Desc")}
              <ul className="mt-1.5 list-disc space-y-1 pl-6 text-base leading-relaxed text-muted-foreground">
                <li>{t("legal.terms.s22Item1Sub2")}</li>
                <li>{t("legal.terms.s26Item1Sub2")}</li>
                <li>{t("legal.terms.s22Item1Sub3")}</li>
                <li>{t("legal.terms.s26Item1Sub4")}</li>
              </ul>
            </li>
            <li><strong className="text-foreground">{t("legal.terms.s26Item2")}</strong>{t("legal.terms.s26Item2Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s26Item3")}</strong>{t("legal.terms.s26Item3Desc")}</li>
          </UL>
        </Section>
      </Part>

      <Part label={t("legal.terms.part5Label")} title={t("legal.terms.part5Title")}>
        <Section title={t("legal.terms.s27Title")}>
          <P>{t("legal.terms.s27Body")}</P>
        </Section>

        <Section title={t("legal.terms.s28Title")}>
          <UL>
            <li><strong className="text-foreground">{t("legal.terms.s28Item1")}</strong>{t("legal.terms.s28Item1Desc")}
              <ul className="mt-1.5 list-disc space-y-1 pl-6 text-base leading-relaxed text-muted-foreground">
                <li>{t("legal.terms.s28Item1Sub1")}</li>
                <li>{t("legal.terms.s28Item1Sub2")}</li>
                <li>{t("legal.terms.s28Item1Sub3")}</li>
              </ul>
            </li>
            <li><strong className="text-foreground">{t("legal.terms.s28Item2")}</strong>{t("legal.terms.s28Item2Desc")}</li>
          </UL>
        </Section>

        <Section title={t("legal.terms.s29Title")}>
          <UL>
            <li><strong className="text-foreground">{t("legal.terms.s29Item1")}</strong>{t("legal.terms.s29Item1Desc")}
              <ul className="mt-1.5 list-disc space-y-1 pl-6 text-base leading-relaxed text-muted-foreground">
                <li>{t("legal.terms.s29Item1Sub1")}</li>
                <li>{t("legal.terms.s29Item1Sub2")}</li>
                <li>{t("legal.terms.s29Item1Sub3")}</li>
              </ul>
            </li>
            <li><strong className="text-foreground">{t("legal.terms.s29Item2")}</strong>{t("legal.terms.s29Item2Desc")}
              <ul className="mt-1.5 list-disc space-y-1 pl-6 text-base leading-relaxed text-muted-foreground">
                <li>{t("legal.terms.s29Item2Sub1")}</li>
                <li>{t("legal.terms.s29Item2Sub2")}</li>
                <li>{t("legal.terms.s29Item2Sub3")}</li>
              </ul>
            </li>
            <li><strong className="text-foreground">{t("legal.terms.s29Item3")}</strong>{t("legal.terms.s29Item3Desc")}</li>
          </UL>
        </Section>

        <Section title={t("legal.terms.s30Title")}>
          <UL>
            <li><strong className="text-foreground">{t("legal.terms.s30Item1")}</strong>{t("legal.terms.s30Item1Desc")}
              <ul className="mt-1.5 list-disc space-y-1 pl-6 text-base leading-relaxed text-muted-foreground">
                <li>{t("legal.terms.s30Item1Sub1")}</li>
                <li>{t("legal.terms.s30Item1Sub2")}</li>
                <li>{t("legal.terms.s30Item1Sub3")}</li>
                <li>{t("legal.terms.s30Item1Sub4")}</li>
                <li>{t("legal.terms.s30Item1Sub5")}</li>
              </ul>
            </li>
            <li><strong className="text-foreground">{t("legal.terms.s30Item2")}</strong>{t("legal.terms.s30Item2Desc")}
              <ul className="mt-1.5 list-disc space-y-1 pl-6 text-base leading-relaxed text-muted-foreground">
                <li>{t("legal.terms.s30Item2Sub1")}</li>
                <li>{t("legal.terms.s30Item2Sub2")}</li>
                <li>{t("legal.terms.s30Item2Sub3")}</li>
                <li>{t("legal.terms.s30Item2Sub4")}</li>
              </ul>
            </li>
            <li><strong className="text-foreground">{t("legal.terms.s30Item3")}</strong>{t("legal.terms.s30Item3Desc")}</li>
          </UL>
        </Section>

        <Section title={t("legal.terms.s31Title")}>
          <UL>
            <li><strong className="text-foreground">{t("legal.terms.s31Item1")}</strong>{t("legal.terms.s31Item1Desc")}
              <ul className="mt-1.5 list-disc space-y-1 pl-6 text-base leading-relaxed text-muted-foreground">
                <li>{t("legal.terms.s31Item1Sub1")}</li>
                <li>{t("legal.terms.s31Item1Sub2")}</li>
                <li>{t("legal.terms.s31Item1Sub3")}</li>
              </ul>
            </li>
            <li><strong className="text-foreground">{t("legal.terms.s31Item2")}</strong>{t("legal.terms.s31Item2Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s31Item3")}</strong>{t("legal.terms.s31Item3Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s31Item4")}</strong>{t("legal.terms.s31Item4Desc")}</li>
          </UL>
        </Section>
      </Part>

      <Part label={t("legal.terms.part6Label")} title={t("legal.terms.part6Title")}>
        <Section title={t("legal.terms.s32Title")}>
          <UL>
            <li><strong className="text-foreground">{t("legal.terms.s32Item1")}</strong>{t("legal.terms.s32Item1Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s32Item2")}</strong>{t("legal.terms.s32Item2Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s32Item3")}</strong>{t("legal.terms.s32Item3Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s32Item4")}</strong>{t("legal.terms.s32Item4Desc")}
              <ul className="mt-1.5 list-disc space-y-1 pl-6 text-base leading-relaxed text-muted-foreground">
                <li>{t("legal.terms.s32Item4Sub1")}</li>
                <li>{t("legal.terms.s32Item4Sub2")}</li>
                <li>{t("legal.terms.s32Item4Sub3")}</li>
              </ul>
            </li>
            <li><strong className="text-foreground">{t("legal.terms.s32Item5")}</strong>{t("legal.terms.s32Item5Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s32Item6")}</strong>{t("legal.terms.s32Item6Desc")}</li>
            <li><strong className="text-foreground">{t("legal.terms.s32Item7")}</strong>{t("legal.terms.s32Item7Desc")}</li>
          </UL>
        </Section>
      </Part>

      <Part label={t("legal.terms.part7Label")} title={t("legal.terms.part7Title")}>
        <Section title={t("legal.terms.s33Title")}>
          <P>{t("legal.terms.s33Body")}</P>
        </Section>

        <Section title={t("legal.terms.s34Title")}>
          <P>{t("legal.terms.s34Body")}</P>
          <div className="flex flex-wrap gap-2 pt-1">
            <a href="mailto:support@HornDeliver.app">
              <ContactBadge icon={Mail}>support@HornDeliver.app</ContactBadge>
            </a>
            <ContactBadge icon={Shield}>{t("legal.terms.s34TrustSafety")}</ContactBadge>
            <ContactBadge icon={Building2}>{t("legal.privacy.s9HornDigital")}</ContactBadge>
          </div>
        </Section>
      </Part>
    </LegalPage>
  );
}

// ============================================================================
// Data Protection
// ============================================================================

export function DataProtectionView() {
  const { t } = useTranslation();
  return (
    <LegalPage
      title={t("legal.dataProtectionTitle")}
      icon={Lock}
      updated="2026-01-15"
      intro={t("legal.dataProtection.intro")}
    >
      <Section title={t("legal.dataProtection.s1Title")}>
        <P>{t("legal.dataProtection.s1Body1")}</P>
        <P>{t("legal.dataProtection.s1Body2")}</P>
      </Section>

      <Section title={t("legal.dataProtection.s2Title")}>
        <P>{t("legal.dataProtection.s2Body")}</P>
        <UL>
          <li><strong className="text-foreground">{t("legal.dataProtection.s2Item1")}</strong>{t("legal.dataProtection.s2Item1Desc")}</li>
          <li><strong className="text-foreground">{t("legal.dataProtection.s2Item2")}</strong>{t("legal.dataProtection.s2Item2Desc")}</li>
          <li><strong className="text-foreground">{t("legal.dataProtection.s2Item3")}</strong>{t("legal.dataProtection.s2Item3Desc")}</li>
          <li><strong className="text-foreground">{t("legal.dataProtection.s2Item4")}</strong>{t("legal.dataProtection.s2Item4Desc")}</li>
        </UL>
      </Section>

      <Section title={t("legal.dataProtection.s3Title")}>
        <P>{t("legal.dataProtection.s3Body1")}</P>
        <UL>
          <li>{t("legal.dataProtection.s3Item1")}</li>
          <li>{t("legal.dataProtection.s3Item2")}</li>
          <li>{t("legal.dataProtection.s3Item3")}</li>
          <li>{t("legal.dataProtection.s3Item4")}</li>
        </UL>
        <P>{t("legal.dataProtection.s3Body2")}</P>
      </Section>

      <Section title={t("legal.dataProtection.s4Title")}>
        <P>{t("legal.dataProtection.s4Body1")} <strong className="text-foreground">{t("legal.dataProtection.s4Body1Emphasis")}</strong> {t("legal.dataProtection.s4Body1Suffix")}</P>
        <P>{t("legal.dataProtection.s4Body2")}</P>
        <UL>
          <li>{t("legal.dataProtection.s4Item1")}</li>
          <li>{t("legal.dataProtection.s4Item2")}</li>
          <li>{t("legal.dataProtection.s4Item3")}</li>
        </UL>
      </Section>

      <Section title={t("legal.dataProtection.s5Title")}>
        <UL>
          <li><strong className="text-foreground">{t("legal.dataProtection.s5Item1")}</strong>{t("legal.dataProtection.s5Item1Desc")}</li>
          <li><strong className="text-foreground">{t("legal.dataProtection.s5Item2")}</strong>{t("legal.dataProtection.s5Item2Desc")}</li>
          <li><strong className="text-foreground">{t("legal.dataProtection.s5Item3")}</strong>{t("legal.dataProtection.s5Item3Desc")}</li>
          <li><strong className="text-foreground">{t("legal.dataProtection.s5Item4")}</strong>{t("legal.dataProtection.s5Item4Desc")}</li>
          <li><strong className="text-foreground">{t("legal.dataProtection.s5Item5")}</strong>{t("legal.dataProtection.s5Item5Desc")}</li>
          <li><strong className="text-foreground">{t("legal.dataProtection.s5Item6")}</strong>{t("legal.dataProtection.s5Item6Desc")}</li>
        </UL>
      </Section>

      <Section title={t("legal.dataProtection.s6Title")}>
        <P>{t("legal.dataProtection.s6Body1")}</P>
        <UL>
          <li>{t("legal.dataProtection.s6Item1")} <strong className="text-foreground">{t("legal.dataProtection.s6Item1Emphasis")}</strong>{t("legal.dataProtection.s6Item1Suffix")}</li>
          <li>{t("legal.dataProtection.s6Item2")}</li>
          <li>{t("legal.dataProtection.s6Item3")}</li>
          <li>{t("legal.dataProtection.s6Item4")}</li>
          <li>{t("legal.dataProtection.s6Item5")}</li>
        </UL>
      </Section>

      <Section title={t("legal.dataProtection.s7Title")}>
        <P>{t("legal.dataProtection.s7Body1")}</P>
        <UL>
          <li>{t("legal.dataProtection.s7Item1")}</li>
          <li>{t("legal.dataProtection.s7Item2")} <strong className="text-foreground">{t("legal.dataProtection.s7Item2Emphasis")}</strong> {t("legal.dataProtection.s7Item2Suffix")}</li>
          <li>{t("legal.dataProtection.s7Item3")} <strong className="text-foreground">{t("legal.dataProtection.s7Item3Emphasis")}</strong>{t("legal.dataProtection.s7Item3Suffix")}</li>
          <li>{t("legal.dataProtection.s7Item4")} <strong className="text-foreground">{t("legal.dataProtection.s7Item4Emphasis")}</strong> {t("legal.dataProtection.s7Item4Suffix")}</li>
          <li>{t("legal.dataProtection.s7Item5")}</li>
        </UL>
      </Section>

      <Section title={t("legal.dataProtection.s8Title")}>
        <P>{t("legal.dataProtection.s8Body")}</P>
        <UL>
          <li>{t("legal.dataProtection.s8Item1")} <strong className="text-foreground">{t("legal.dataProtection.s8Item1Emphasis")}</strong> {t("legal.dataProtection.s8Item1Suffix")}</li>
          <li>{t("legal.dataProtection.s8Item2")}</li>
          <li>{t("legal.dataProtection.s8Item3")}</li>
          <li>{t("legal.dataProtection.s8Item4")}</li>
        </UL>
      </Section>

      <Section title={t("legal.dataProtection.s9Title")}>
        <P>{t("legal.dataProtection.s9Body1")} <a href="mailto:support@HornDeliver.app" className="font-medium text-primary underline">support@HornDeliver.app</a>{t("legal.dataProtection.s9Body1Suffix")}</P>
        <UL>
          <li><strong className="text-foreground">{t("legal.dataProtection.s9Item1")}</strong>{t("legal.dataProtection.s9Item1Desc")}</li>
          <li><strong className="text-foreground">{t("legal.dataProtection.s9Item2")}</strong>{t("legal.dataProtection.s9Item2Desc")}</li>
          <li><strong className="text-foreground">{t("legal.dataProtection.s9Item3")}</strong>{t("legal.dataProtection.s9Item3Desc")}</li>
          <li><strong className="text-foreground">{t("legal.dataProtection.s9Item4")}</strong>{t("legal.dataProtection.s9Item4Desc")}</li>
          <li><strong className="text-foreground">{t("legal.dataProtection.s9Item5")}</strong>{t("legal.dataProtection.s9Item5Desc")}</li>
          <li><strong className="text-foreground">{t("legal.dataProtection.s9Item6")}</strong>{t("legal.dataProtection.s9Item6Desc")}</li>
        </UL>
        <P>{t("legal.privacy.s5ClosingNote")}</P>
      </Section>

      <Section title={t("legal.dataProtection.s10Title")}>
        <P>{t("legal.dataProtection.s10Body")}</P>
        <UL>
          <li><strong className="text-foreground">{t("legal.dataProtection.s10Item1")}</strong>{t("legal.dataProtection.s10Item1Desc")}</li>
          <li><strong className="text-foreground">{t("legal.dataProtection.s10Item2")}</strong>{t("legal.dataProtection.s10Item2Desc")}</li>
          <li><strong className="text-foreground">{t("legal.dataProtection.s10Item3")}</strong>{t("legal.dataProtection.s10Item3Desc")}</li>
          <li><strong className="text-foreground">{t("legal.dataProtection.s10Item4")}</strong>{t("legal.dataProtection.s10Item4Desc")}</li>
        </UL>
      </Section>

      <Section title={t("legal.dataProtection.s11Title")}>
        <P>{t("legal.dataProtection.s11Body")}</P>
        <div className="flex flex-wrap gap-2 pt-1">
          <a href="mailto:support@HornDeliver.app">
            <ContactBadge icon={Mail}>support@HornDeliver.app</ContactBadge>
          </a>
          <ContactBadge icon={Phone}>+253 77 XX XX XX</ContactBadge>
          <ContactBadge icon={Building2}>{t("legal.privacy.s9HornDigital")}</ContactBadge>
        </div>
      </Section>
    </LegalPage>
  );
}

// ============================================================================
// Help & Support
// ============================================================================

export function SupportView() {
  const navigate = useUiStore((s) => s.navigate);
  const { t } = useTranslation();
  const { user } = useAuthStore();

  const handleRoute = (route: string) => {
    // Track / app routes require auth. If not logged in, send to login first.
    if (!user) {
      navigate("login");
      return;
    }
    navigate(route);
  };

  const helpCards = [
    {
      icon: Package,
      title: t("legal.support.cardTrackTitle"),
      desc: t("legal.support.cardTrackDesc"),
      action: t("legal.support.cardTrackAction"),
      mailto: null as string | null,
      route: "app/track" as string | null,
    },
    {
      icon: CreditCard,
      title: t("legal.support.cardPaymentTitle"),
      desc: t("legal.support.cardPaymentDesc"),
      action: t("legal.support.cardPaymentAction"),
      mailto: "mailto:support@HornDeliver.app?subject=Payment%20issue",
      route: null as string | null,
    },
    {
      icon: UserCog,
      title: t("legal.support.cardAccountTitle"),
      desc: t("legal.support.cardAccountDesc"),
      action: t("legal.support.cardAccountAction"),
      mailto: "mailto:support@HornDeliver.app?subject=Account%20issue",
      route: null as string | null,
    },
    {
      icon: Flag,
      title: t("legal.support.cardReportTitle"),
      desc: t("legal.support.cardReportDesc"),
      action: t("legal.support.cardReportAction"),
      mailto: "mailto:support@HornDeliver.app?subject=Report%20a%20user",
      route: null as string | null,
    },
    {
      icon: Scale,
      title: t("legal.support.cardDisputeTitle"),
      desc: t("legal.support.cardDisputeDesc"),
      action: t("legal.support.cardDisputeAction"),
      mailto: "mailto:support@HornDeliver.app?subject=Job%20dispute",
      route: null as string | null,
    },
  ];

  // How-to guides — step-by-step walkthroughs for the core user flows.
  const howTos = [
    {
      title: t("legal.support.guide1Title"),
      icon: Package,
      steps: [
        "Open the app and tap the Home tab.",
        "Tap 'New Pickup & Deliver' to send an existing item, or 'Order from Store' to buy from a merchant.",
        "Enter the pickup and dropoff addresses.",
        "Describe your parcel (size, weight, fragile, category).",
        "Get an instant delivery-fee quote and confirm.",
        "A courier is assigned — track it live in the Track tab.",
      ],
    },
    {
      title: t("legal.support.guide2Title"),
      icon: ShoppingCart,
      steps: [
        "Tap the Stores tab on your home screen.",
        "Browse or search for a restaurant, grocery, or pharmacy near you.",
        "Open the store and add items to your cart.",
        "Review your cart and checkout with your delivery address.",
        "A courier picks up your order and delivers it to your door.",
        "Pay with cash on delivery or mobile money.",
      ],
    },
    {
      title: t("legal.support.guide3Title"),
      icon: Truck,
      steps: [
        "Go to Profile → 'Become Provider/Courier'.",
        "Select the Courier role and submit your application.",
        "Upload your driving licence, vehicle photo, and insurance document.",
        "Wait for admin approval (usually 24–48 hours).",
        "Once approved, switch to the Courier role in the More menu.",
        "Browse available jobs on the Find tab and tap Accept to start earning.",
      ],
    },
    {
      title: t("legal.support.guide4Title"),
      icon: Navigation,
      steps: [
        "Open the More menu → tap 'Track a Delivery'.",
        "Or open Help & Support → 'Track a delivery' → 'Open tracking'.",
        "See your active deliveries with a live progress timeline.",
        "View the courier's name, rating, and vehicle type.",
        "Tap Chat or Call to reach your courier directly.",
        "Check the ETA and your confirmation code for cash payments.",
      ],
    },
    {
      title: t("legal.support.guide5Title"),
      icon: CreditCard,
      steps: [
        "Go to Profile → Mobile Money and add your account.",
        "Choose your provider: D Money, eBirr, Zaad, or Waafi.",
        "Enter and verify your account number.",
        "When a delivery is assigned, tap Pay on the job detail page.",
        "Select mobile money as the payment method.",
        "Confirm the transaction — your courier gets paid instantly.",
      ],
    },
    {
      title: t("legal.support.guide6Title"),
      icon: Star,
      steps: [
        "After your delivery is marked 'Delivered', you'll be prompted to rate.",
        "Give 1–5 stars based on your experience.",
        "Leave an optional written comment.",
        "Your rating helps build trust on the platform.",
        "Couriers with consistently low ratings are automatically reviewed.",
        "You can also rate from the job detail page anytime.",
      ],
    },
  ];

  const faqs = [
    {
      q: t("legal.support.faq1Q"),
      a: t("legal.support.faq1A"),
    },
    {
      q: t("legal.support.faq2Q"),
      a: t("legal.support.faq2A"),
    },
    {
      q: t("legal.support.faq3Q"),
      a: t("legal.support.faq3A"),
    },
    {
      q: t("legal.support.faq4Q"),
      a: t("legal.support.faq4A"),
    },
    {
      q: t("legal.support.faq5Q"),
      a: t("legal.support.faq5A"),
    },
    {
      q: t("legal.support.faq6Q"),
      a: t("legal.support.faq6A"),
    },
  ];

  const services = [
    { name: t("verticals.parcel"), icon: Package, djibouti: true, ethiopia: true, somaliland: true },
    { name: t("verticals.food"), icon: Banknote, djibouti: true, ethiopia: true, somaliland: true },
    { name: t("verticals.grocery"), icon: ShoppingCart, djibouti: true, ethiopia: true, somaliland: true },
    { name: t("verticals.pharmacy"), icon: Pill, djibouti: true, ethiopia: true, somaliland: false },
    { name: t("verticals.water"), icon: Droplets, djibouti: true, ethiopia: true, somaliland: false },
    { name: t("verticals.courier"), icon: Truck, djibouti: true, ethiopia: true, somaliland: true },
    { name: t("verticals.errand"), icon: MessageSquare, djibouti: true, ethiopia: true, somaliland: true },
  ];

  return (
    <LegalPage
      title={t("legal.supportTitle")}
      icon={LifeBuoy}
      intro={t("legal.support.intro")}
    >
      <Section title={t("legal.support.s1Title")}>
        <P>{t("legal.support.s1Body")}</P>
        <div className="grid grid-cols-1 gap-3 pt-2 sm:grid-cols-2">
          {helpCards.map((card) => {
            const Icon = card.icon;
            return (
              <Card key={card.title} className="py-0">
                <CardContent className="space-y-2 p-4">
                  <div className="flex items-center gap-2">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <Icon className="h-5 w-5" />
                    </div>
                    <h3 className="text-sm font-semibold">{card.title}</h3>
                  </div>
                  <P>{card.desc}</P>
                  <div className="pt-1">
                    {card.route ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1.5 border-primary/40 text-primary hover:bg-primary/5"
                        onClick={() => handleRoute(card.route!)}
                      >
                        <Navigation className="h-3.5 w-3.5" />
                        {card.action}
                        <ArrowRight className="h-3.5 w-3.5" />
                      </Button>
                    ) : card.mailto ? (
                      <a href={card.mailto}>
                        <Button size="sm" variant="outline" className="gap-1.5">
                          <Mail className="h-3.5 w-3.5" />
                          {card.action}
                        </Button>
                      </a>
                    ) : (
                      <Badge variant="secondary" className="gap-1.5 py-1">
                        <Clock className="h-3.5 w-3.5" />
                        {card.action}
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </Section>

      <Section title={t("legal.support.s3Title")}>
        <div className="space-y-3 pt-1">
          <div className="flex flex-col gap-2 rounded-lg border border-border/60 bg-muted/30 p-4 sm:flex-row sm:items-center sm:gap-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Mail className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-foreground">{t("legal.support.s3EmailLabel")}</p>
              <P>
                <a href="mailto:support@HornDeliver.app" className="font-medium text-primary underline">support@HornDeliver.app</a>
                {" "}{t("legal.support.s3EmailDesc")}
              </P>
            </div>
          </div>

          <div className="flex flex-col gap-2 rounded-lg border border-border/60 bg-muted/30 p-4 sm:flex-row sm:items-center sm:gap-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Phone className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-foreground">{t("legal.support.s3PhoneLabel")}</p>
              <P>
                <a href="tel:+25377000000" className="font-medium text-primary underline">+253 77 XX XX XX</a>
                {" "}{t("legal.support.s3PhoneDesc")}
              </P>
            </div>
          </div>

          <div className="flex flex-col gap-2 rounded-lg border border-border/60 bg-muted/30 p-4 sm:flex-row sm:items-center sm:gap-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Bell className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-foreground">{t("legal.support.s3InAppLabel")}</p>
              <P>
                {t("legal.support.s3InAppDesc")} <Bell className="inline h-3.5 w-3.5" /> {t("legal.support.s3InAppDescMid")} <strong className="text-foreground">{t("legal.support.s3InAppDescEmphasis")}</strong> {t("legal.support.s3InAppDescSuffix")}
              </P>
            </div>
          </div>
        </div>
      </Section>

      <Section title={t("legal.support.s2Title")}>
        <P>{t("legal.support.s2Body")}</P>
        <Accordion type="single" collapsible className="w-full pt-2">
          {howTos.map((guide, i) => {
            const GIcon = guide.icon;
            return (
              <AccordionItem key={i} value={`howto-${i}`}>
                <AccordionTrigger className="text-left text-base font-medium text-foreground">
                  <span className="flex items-center gap-2.5">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <GIcon className="h-4 w-4" />
                    </span>
                    {guide.title}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="text-base leading-relaxed text-muted-foreground">
                  <ol className="space-y-2 pt-1">
                    {guide.steps.map((step, si) => (
                      <li key={si} className="flex items-start gap-3">
                        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/15 text-xs font-bold text-primary">
                          {si + 1}
                        </span>
                        <span className="pt-0.5">{step}</span>
                      </li>
                    ))}
                  </ol>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </Section>

      <Section title={t("legal.support.s4Title")}>
        <P>{t("legal.support.s4Body")}</P>
        <div className="overflow-hidden rounded-lg border border-border/60">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-foreground">{t("legal.support.s4ColService")}</TableHead>
                <TableHead className="text-center">{t("legal.support.s4ColDjibouti")}</TableHead>
                <TableHead className="text-center">{t("legal.support.s4ColEthiopia")}</TableHead>
                <TableHead className="text-center">{t("legal.support.s4ColSomaliland")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {services.map((svc) => {
                const Icon = svc.icon;
                return (
                  <TableRow key={svc.name}>
                    <TableCell className="font-medium text-foreground">
                      <span className="inline-flex items-center gap-2">
                        <Icon className="h-4 w-4 text-muted-foreground" />
                        {svc.name}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      {svc.djibouti ? (
                        <CheckCircle2 className="mx-auto h-4 w-4 text-emerald-600" />
                      ) : (
                        <XCircle className="mx-auto h-4 w-4 text-muted-foreground/40" />
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {svc.ethiopia ? (
                        <CheckCircle2 className="mx-auto h-4 w-4 text-emerald-600" />
                      ) : (
                        <XCircle className="mx-auto h-4 w-4 text-muted-foreground/40" />
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {svc.somaliland ? (
                        <CheckCircle2 className="mx-auto h-4 w-4 text-emerald-600" />
                      ) : (
                        <XCircle className="mx-auto h-4 w-4 text-muted-foreground/40" />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        <div className="flex flex-wrap gap-2 pt-2 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> {t("legal.support.s4LegendLive")}</span>
          <span className="inline-flex items-center gap-1"><XCircle className="h-3.5 w-3.5 text-muted-foreground/40" /> {t("legal.support.s4LegendNotAvailable")}</span>
        </div>
      </Section>

      <Section title={t("legal.support.s5Title")}>
        <Accordion type="single" collapsible className="w-full">
          {faqs.map((faq, i) => (
            <AccordionItem key={i} value={`item-${i}`}>
              <AccordionTrigger className="text-left text-base font-medium text-foreground">
                {faq.q}
              </AccordionTrigger>
              <AccordionContent className="text-base leading-relaxed text-muted-foreground">
                {faq.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Section>

      <Section title={t("legal.support.s6Title")}>
        <P>{t("legal.support.s6Body")}</P>
        <div className="pt-1">
          <a href="mailto:support@HornDeliver.app?subject=HornDeliver%20feedback">
            <Button variant="outline" className="gap-2">
              <Mail className="h-4 w-4" />
              support@HornDeliver.app
            </Button>
          </a>
        </div>
        <P className="text-sm pt-1">{t("legal.support.s6BugNote")}</P>
      </Section>

      <Section title={t("legal.support.s7Title")}>
        <div className="flex flex-col gap-3 rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 sm:flex-row sm:items-start sm:gap-3">
          <AlertCircle className="h-5 w-5 shrink-0 text-amber-600" />
          <div className="space-y-1">
            <p className="text-sm font-semibold text-foreground">{t("legal.support.s7AlertTitle")}</p>
            <P>{t("legal.support.s7AlertBody1")}</P>
            <P>{t("legal.support.s7AlertBody2")}</P>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 pt-3">
          <ContactBadge icon={Globe}>{t("legal.support.s7OperatingCountries")}</ContactBadge>
          <ContactBadge icon={Clock}>{t("legal.support.s7UptimeTarget")}</ContactBadge>
          <ContactBadge icon={MapPin}>{t("legal.support.s7Servers")}</ContactBadge>
        </div>
      </Section>
    </LegalPage>
  );
}
